import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from '../api/session';
import { Spinner } from '../ui/Spinner/Spinner';
import { AppShell } from './AppShell';
import { adminRoutes } from '../features/admin';

// Code-split per screen. `/login` is loaded eagerly (small, and it's the
// first thing an unauthenticated visitor needs) — everything behind
// `RequireAuth` is lazy so an end user's first load doesn't pull in every
// authenticated screen up front.
import LoginScreen from '../screens/LoginScreen';

const DashboardScreen = lazy(() => import('../screens/DashboardScreen'));
const TicketsScreen = lazy(() => import('../screens/TicketsScreen'));
const TicketDetailScreen = lazy(() => import('../screens/TicketDetailScreen'));
const NewTicketScreen = lazy(() => import('../features/new-ticket/NewTicketScreen'));
const MyTicketsScreen = lazy(() => import('../screens/MyTicketsScreen'));
const UnassignedScreen = lazy(() => import('../screens/UnassignedScreen'));
const AwaitingConfirmationScreen = lazy(() => import('../screens/AwaitingConfirmationScreen'));
const ResolvedScreen = lazy(() => import('../screens/ResolvedScreen'));
const ClosedScreen = lazy(() => import('../screens/ClosedScreen'));
const NotificationsScreen = lazy(() => import('../features/notifications/NotificationsScreen'));
const IncidentListScreen = lazy(() => import('../features/incidents/IncidentListScreen'));
const IncidentFormScreen = lazy(() => import('../features/incidents/IncidentFormScreen'));
const InvoiceSubmissionScreen = lazy(() => import('../features/invoices/InvoiceSubmissionScreen'));
const ItChecklistScreen = lazy(() => import('../features/it-checklist/ItChecklistScreen'));
const ItChecklistMonitorScreen = lazy(() => import('../features/it-checklist-monitor/ItChecklistMonitorScreen'));
const DesignWorkflowScreen = lazy(() => import('../features/design-workflow/DesignWorkflowScreen'));
const DesignRequestFormScreen = lazy(() => import('../features/design-workflow/DesignRequestFormScreen'));
const DesignRequestDetailScreen = lazy(() => import('../features/design-workflow/DesignRequestDetailScreen'));
const NotFoundScreen = lazy(() => import('../screens/NotFoundScreen'));
// Public (no session): the resolution-email landing page. The signed,
// single-use token in its link is the credential; see the screen's doc.
const ConfirmResolutionScreen = lazy(() => import('../features/confirm/ConfirmResolutionScreen'));

function RouteLoadingFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
      <Spinner size="lg" label="Loading" />
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/confirm" element={<ConfirmResolutionScreen />} />

          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardScreen />} />
            <Route path="tickets" element={<TicketsScreen />} />
            <Route path="tickets/new" element={<NewTicketScreen />} />
            <Route path="tickets/:id" element={<TicketDetailScreen />} />
            <Route path="my-tickets" element={<MyTicketsScreen />} />
            <Route path="unassigned" element={<UnassignedScreen />} />
            <Route path="awaiting-confirmation" element={<AwaitingConfirmationScreen />} />
            <Route path="resolved" element={<ResolvedScreen />} />
            <Route path="closed" element={<ClosedScreen />} />
            <Route path="notifications" element={<NotificationsScreen />} />
            <Route path="incidents" element={<IncidentListScreen />} />
            <Route path="incidents/new" element={<IncidentFormScreen />} />
            <Route path="incidents/:id" element={<IncidentFormScreen />} />
            <Route path="invoices" element={<InvoiceSubmissionScreen />} />
            <Route path="it-checklist" element={<ItChecklistScreen />} />
            <Route path="it-checklist-monitor" element={<ItChecklistMonitorScreen />} />
            <Route path="design-workflow" element={<DesignWorkflowScreen />} />
            <Route path="design-workflow/new" element={<DesignRequestFormScreen />} />
            <Route path="design-workflow/:id/edit" element={<DesignRequestFormScreen />} />
            <Route path="design-workflow/:id" element={<DesignRequestDetailScreen />} />
            {/* Admin area (features/admin). `/admin` itself redirects to the users screen. */}
            <Route path="admin" element={<Navigate to="/admin/users" replace />} />
            {adminRoutes.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            <Route path="*" element={<NotFoundScreen />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
